import { useCallback, useEffect, useMemo, useState } from 'react';
import { userService } from '@/services';
import type { AhaWalkthroughState, UserProfile } from '@/types';
import { useGmailConnectionStatus } from './useGmailConnectionStatus';

export interface UseAhaWalkthroughOptions {
  householdId: string | null | undefined;
}

export interface UseAhaWalkthroughResult {
  /** Current persisted state. `null` while the profile is loading. */
  state: AhaWalkthroughState | null;
  /**
   * True iff the walkthrough should auto-open on this mount: Gmail is
   * connected, onboarding is complete, and state is still 'pending'.
   *
   * Manual re-entry from Settings does NOT use this flag — it should
   * call `open()` directly regardless of state.
   */
  shouldAutoShow: boolean;
  isLoading: boolean;
  /** Persist a transition to 'started'. Idempotent. */
  markStarted: () => Promise<void>;
  /** Persist a transition to 'dismissed'. Terminal. */
  markDismissed: () => Promise<void>;
  /** Persist a transition to 'completed'. Terminal. */
  markCompleted: () => Promise<void>;
}

/**
 * Today Tab walkthrough lifecycle hook (YAR-238).
 *
 * Reads `users.aha_walkthrough_state` and the household's Gmail
 * connection status. Auto-trigger only fires once: when the user lands
 * on Today with Gmail connected and the state is still 'pending'. Once
 * we transition to 'started' / 'dismissed' / 'completed' the auto-show
 * stays off; re-entry has to come from Settings.
 *
 * Force-quit semantics (AC 4): Step 0 doesn't persist anything on
 * mount, so a user who closes the tab before tapping any CTA stays
 * 'pending' and re-triggers on next visit. Only the action callbacks
 * below write state.
 */
export function useAhaWalkthrough(
  options: UseAhaWalkthroughOptions,
): UseAhaWalkthroughResult {
  const [profile, setProfile] = useState<UserProfile | null | undefined>(
    undefined,
  );
  const gmail = useGmailConnectionStatus(options.householdId);

  useEffect(() => {
    let cancelled = false;
    userService
      .getProfile()
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const state = useMemo<AhaWalkthroughState | null>(() => {
    if (profile === undefined) return null;
    if (!profile) return null;
    const raw = profile.aha_walkthrough_state;
    if (
      raw === 'pending' ||
      raw === 'started' ||
      raw === 'dismissed' ||
      raw === 'completed'
    ) {
      return raw;
    }
    // Defensive default — CHECK constraint should make this unreachable
    // but a future migration drift shouldn't crash the Today Tab.
    return 'pending';
  }, [profile]);

  const isLoading = profile === undefined || gmail.isLoading;

  const shouldAutoShow =
    !isLoading &&
    state === 'pending' &&
    profile?.onboarding_completed_at !== null &&
    profile?.onboarding_completed_at !== undefined &&
    gmail.data?.state === 'connected';

  const writeState = useCallback(async (next: AhaWalkthroughState) => {
    const updated = await userService.updateProfile({
      aha_walkthrough_state: next,
    });
    setProfile(updated);
  }, []);

  const markStarted = useCallback(() => writeState('started'), [writeState]);
  const markDismissed = useCallback(
    () => writeState('dismissed'),
    [writeState],
  );
  const markCompleted = useCallback(
    () => writeState('completed'),
    [writeState],
  );

  return {
    state,
    shouldAutoShow,
    isLoading,
    markStarted,
    markDismissed,
    markCompleted,
  };
}
